import type { Issue } from './types';
import { PRIORITY_ORDER, SIZE_ORDER } from './types';
import { stillBlocked } from './blocked';

/**
 * What can actually be picked up right now, best first.
 *
 * Open, with nothing holding it back, most urgent first. Among equally urgent
 * work the shorter job goes first: it finishes, and finishing is what moves a
 * board. Recency is the last resort, because "touched recently" is not a reason
 * to do anything.
 *
 * WHY THIS IS ITS OWN FILE
 *
 * The board answers "what next" for the whole project; an epic answers it for
 * its own work. Those are the same question asked of different sets, and two
 * copies of the rule would drift — the way the two progress percentages did,
 * where the headline and the epic rows disagreed for weeks and both looked
 * plausible.
 */
export function readyFirst(issues: Issue[], pool: Issue[] = issues): Issue[] {
  const rank = (i: Issue) => ({
    p: PRIORITY_ORDER.indexOf(i.priority),
    s: i.size && SIZE_ORDER.includes(i.size) ? SIZE_ORDER.indexOf(i.size) : SIZE_ORDER.length,
  });
  return issues
    /*
     * `stillBlocked` rather than `blocked`, because a sentence saying "waiting
     * on #116" does not stop being there when #116 is finished — and a stale
     * blocker hides real, startable work from the one place somebody looks for
     * it. See src/blocked.ts.
     *
     * `pool` is the board `stillBlocked` resolves card references against, and it
     * must be the WHOLE board, not the set being ranked. When the caller hands in
     * a pre-narrowed set — the To-Do column, or one sprint — a finished blocker
     * has already left that set, so `entryStillBlocks` cannot find the named card
     * and ("named but not found, never guess it away") keeps the dependent looking
     * blocked. That is exactly how a just-freed card failed to appear in "ready to
     * start" (L164). Defaults to `issues` so the whole-board callers are unchanged.
     */
    .filter((i) => i.status !== 'done' && i.status !== 'wontdo' && !stillBlocked(i, pool))
    .sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      return ra.p - rb.p || ra.s - rb.s || b.updatedAt - a.updatedAt;
    });
}
